import { VISUAL_DIFF_FOREGROUND } from '@ardoq/color-helpers';
import { colors } from '@ardoq/design-tokens';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import styled, { css } from 'styled-components';
import { HIGHLIGHTED_CLASS_NAME, NAVIGATOR_BACKGROUND } from '../consts';

const NavigatorItemIconContainer = styled.span`
  cursor: pointer;
  width: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  user-select: none;
`;
export const NavigatorItemLink = styled(NavigatorItemIconContainer)``;
export const NavigatorItemContextMenu = styled(
  NavigatorItemIconContainer
).attrs({
  role: 'button',
  'aria-label': 'Context menu',
})``;
export const NavigatorItemCloseScenario = styled(NavigatorItemIconContainer)``;
export const NavigatorItemCloseWorkspace = styled(NavigatorItemIconContainer)``;
export const NavigatorItemExpander = styled(NavigatorItemIconContainer).attrs({
  className: 'navigatorItemExpander', // browser tests depend on this class name :( (navigator.test.js)
})`
  margin-right: 4px;
`;

const controlledContainerHeightStyles = css`
  position: absolute;
  height: 100%;
  left: 0;
  top: 0;
`;

const uncontrolledContainerHeightStyles = css`
  min-height: 60px;
`;

export const NavigatorWrapper = styled.div<{
  $backgroundColor?: string;
  $isControlledContainerHeight: boolean;
}>`
  ${({ $isControlledContainerHeight }) =>
    $isControlledContainerHeight
      ? controlledContainerHeightStyles
      : uncontrolledContainerHeightStyles}
  background: ${({ $backgroundColor }) =>
    $backgroundColor ?? NAVIGATOR_BACKGROUND};

  overflow: auto;

  user-select: none;
  width: 100%;
  contain: layout;
  z-index: 1;
  &.no-drop,
  &.no-drop span,
  &.no-drop div {
    cursor: no-drop;
  }

  .is-context-node {
    opacity: 0.5;
  }

  .lock-icon {
    width: 24px;
  }

  .component-name,
  .scenario-name,
  .workspace-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 5px;
    margin-right: 10px;
    width: 18px;
    font-size: 18px;
    line-height: inherit;
  }

  .excluded .expander,
  .excluded .icon-container,
  .excluded .component-name {
    opacity: 0.5;
  }

  &.is-linking .component-item:hover .component-name {
    font-weight: bold;
    color: orange;
  }

  .drag-target {
    cursor: pointer;
  }

  .active-drag-target {
    opacity: 0.5;
  }

  @keyframes flash-highlight {
    0% {
      background-color: hsla(60, 100%, 70%, 1);
    }
    100% {
      background-color: hsla(60, 100%, 70%, 0);
    }
  }

  .highlight-flash {
    animation: flash-highlight 1s;
  }

  &&& .icon-container.visual-diff-added,
  &&& .icon-container.visual-diff-removed,
  &&& .icon-container.visual-diff-changed,
  &&& .icon-container.visual-diff-unchanged {
    fill: ${VISUAL_DIFF_FOREGROUND};
    color: ${VISUAL_DIFF_FOREGROUND};
    width: 24px;
  }
`;

export const WorkspaceIcon = styled(Icon).attrs({
  iconName: IconName.WORKSPACE,
  iconSize: IconSize.SMALL,
})`
  margin-right: 6px;
  fill: ${colors.grey700};
`;

export const ScenarioIcon = styled(Icon).attrs({
  iconSize: IconSize.SMALL,
})`
  margin-right: 6px;
  color: ${colors.grey700};
  fill: ${colors.grey700};
`;

export const LayoutBoxesContainer = styled.div`
  position: absolute;
  width: 100%;
  left: 0;
`;

export const SelectItemContainer = styled.div`
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-radius: 6px;
  align-items: center;

  &:hover,
  &.${HIGHLIGHTED_CLASS_NAME} {
    background-color: ${colors.surfaceHover};
  }

  .keyboard-navigation-active &:hover {
    &:not(.${HIGHLIGHTED_CLASS_NAME}) {
      background-color: inherit;
    }
    &.selected {
      background-color: ${colors.surfaceActive};
    }
  }

  .keyboard-navigation-active &.selected.${HIGHLIGHTED_CLASS_NAME} {
    background-color: ${colors.surfaceActiveHover};
  }

  &.active {
    background-color: ${colors.surfaceSelected};
  }

  &.selected {
    background-color: ${colors.surfaceActive};
    &:hover {
      background-color: ${colors.surfaceActiveHover};
    }
  }

  &.excluded {
    > ${NavigatorItemExpander}, > .icon-container,
    > .component-name {
      opacity: 0.5;
    }
  }
`;
